/**
 * td-cart-addon
 *
 * Compact cart drawer add-on card (snippets/td-cart-addon-card.liquid).
 * - Swatch click selects that color: updates the variant id the theme's <product-form> submits,
 *   the price (and its Intelligems hook), the image and the visible color name.
 * - Adding to cart is handled by the vendor <product-form>, which posts to the cart, re-renders
 *   the drawer in the same request, and shows its own loading/added states and errors.
 * - Announces a successful add through a polite live region that survives drawer re-renders.
 *
 * Loaded once, globally, from snippets/td-globals.liquid, because the drawer HTML is re-injected
 * on every cart change and scripts inside injected HTML would not run.
 */
(() => {
  const LIVE_REGION_ID = 'td-cart-addons-status';

  const SELECTORS = {
    swatch: '[data-td-cart-addon-swatch]',
    variantId: '[data-td-cart-addon-id]',
    /* td-selector-exception: .igPrice is the Intelligems price test hook; Intelligems owns that class */
    price: '[data-td-cart-addon-price] .igPrice',
    color: '[data-td-cart-addon-color]',
    image: '[data-td-cart-addon-media] img',
    error: '[data-td-cart-addon-error]',
  };

  /**
   * Announces a message through a polite live region that outlives drawer re-renders.
   *
   * @param {string | undefined} message - Text to announce; nothing happens when empty.
   * @returns {void}
   */
  const announce = (message) => {
    if (!message) return;
    let region = document.getElementById(LIVE_REGION_ID);
    if (!region) {
      region = document.createElement('div');
      region.id = LIVE_REGION_ID;
      region.className = 'visually-hidden';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('role', 'status');
      document.body.appendChild(region);
    }
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 100);
  };

  /**
   * Cart drawer add-on card: swaps the selected color variant and announces successful adds.
   */
  class TdCartAddon extends HTMLElement {
    /**
     * Binds the click and add-to-cart listeners.
     *
     * @returns {void}
     */
    connectedCallback() {
      this.onClick = this.handleClick.bind(this);
      this.onCartAdd = this.handleCartAdd.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('on:cart:add', this.onCartAdd);
    }

    /**
     * Removes the listeners bound in connectedCallback.
     *
     * @returns {void}
     */
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('on:cart:add', this.onCartAdd);
    }

    /**
     * Selects the clicked swatch, ignoring clicks elsewhere and disabled swatches.
     *
     * @param {MouseEvent} evt - Click event delegated to the card.
     * @returns {void}
     */
    handleClick(evt) {
      const swatch = evt.target.closest(SELECTORS.swatch);
      if (!swatch || !this.contains(swatch) || swatch.disabled) return;
      this.selectSwatch(swatch);
    }

    /**
     * Applies a swatch's variant to the form id, price, color name and image.
     *
     * @param {HTMLElement} swatch - The swatch button carrying the variant data attributes.
     * @returns {void}
     */
    selectSwatch(swatch) {
      this.querySelectorAll(SELECTORS.swatch).forEach((el) => {
        el.setAttribute('aria-pressed', el === swatch ? 'true' : 'false');
      });

      const { variantId, price, color, imageSrc, imageAlt } = swatch.dataset;

      const idInput = this.querySelector(SELECTORS.variantId);
      if (idInput && variantId) idInput.value = variantId;

      const priceEl = this.querySelector(SELECTORS.price);
      if (priceEl) {
        if (variantId) priceEl.dataset.variantId = variantId;
        if (price) priceEl.textContent = price;
      }

      const colorEl = this.querySelector(SELECTORS.color);
      if (colorEl && color) colorEl.textContent = color;

      const image = this.querySelector(SELECTORS.image);
      if (image && imageSrc && image.getAttribute('src') !== imageSrc) {
        // image_tag emits a srcset for the first variant; drop it so the swapped src wins.
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = imageSrc;
        if (imageAlt) image.alt = imageAlt;
      }

      const error = this.querySelector(SELECTORS.error);
      if (error) error.hidden = true;
    }

    /**
     * Announces the card's "added to cart" message after the vendor form adds the item.
     *
     * @returns {void}
     */
    handleCartAdd() {
      announce(this.dataset.addedMessage);
    }
  }

  if (!customElements.get('td-cart-addon')) {
    customElements.define('td-cart-addon', TdCartAddon);
  }
})();
